export type UploadedImage = { id: string; url: string }

const HEADING = /^H[1-6]$/

/**
 * Word separates paragraphs with spacing, which the conversion drops, so an
 * empty line goes between the top-level blocks - except under a heading,
 * which belongs to the text below it. Images that could not be uploaded are
 * left out. Returns null when nothing is left.
 */
export function tidyDocxHtml(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc
    .querySelectorAll('img:not([src]), img[src=""]')
    .forEach(img => img.remove())

  const blocks = [...doc.body.children].filter(
    block => block.textContent?.trim() || block.querySelector('img')
  )
  if (!blocks.length) return null

  return blocks
    .map((block, index) => {
      const previous = blocks[index - 1]
      const spaced = previous && !HEADING.test(previous.tagName)
      return `${spaced ? '<p><br></p>' : ''}${block.outerHTML}`
    })
    .join('')
}

/**
 * Converts a Word document to editor HTML with mammoth, which keeps headings
 * (from Word's heading styles), bold, italic, links, lists and tables, and
 * drops fonts and sizes. Embedded images are uploaded through `uploadImage`.
 * mammoth's browser build is loaded only when a document is imported.
 */
export async function docxToHtml(
  file: File,
  uploadImage: (file: File) => Promise<UploadedImage | null>
): Promise<string | null> {
  const { default: mammoth } = await import('mammoth/mammoth.browser.min.js')

  const { value } = await mammoth.convertToHtml(
    { arrayBuffer: await file.arrayBuffer() },
    {
      convertImage: mammoth.images.imgElement(async image => {
        const type = image.contentType
        const name = `image.${type.split('/')[1] ?? 'png'}`
        const data = await image.readAsArrayBuffer()
        const uploaded = await uploadImage(new File([data], name, { type }))

        const attributes = uploaded
          ? { src: uploaded.url, 'data-id': uploaded.id, width: '100%' }
          : { src: '' }
        return attributes
      }),
    }
  )
  return tidyDocxHtml(value)
}
